import { FC, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Camera, Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface AvatarUploadProps {
  url?: string | null;
  onUpload: (url: string) => Promise<void>;
  name: string;
}

const AVATAR_WORKER_URL = import.meta.env.VITE_AVATAR_WORKER_URL || 'https://avatars.financieramente.kids';

export const AvatarUpload: FC<AvatarUploadProps> = ({ url, onUpload, name }) => {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const uploadAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);

      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('Debes seleccionar una imagen para subir.');
      }

      const file = event.target.files[0];

      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        throw new Error('Tipo de archivo no permitido. Solo se permiten imágenes.');
      }

      // Validar tamaño (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        throw new Error('La imagen es demasiado grande. El tamaño máximo permitido es 2MB.');
      }

      // Subir a R2 a través del worker
      const response = await fetch(AVATAR_WORKER_URL, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
      }

      const { url: avatarUrl } = await response.json();

      await onUpload(avatarUrl);

      toast({
        title: "¡Avatar actualizado!",
        description: "Tu imagen de perfil ha sido actualizada correctamente.",
      });

    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "No se pudo subir la imagen. Por favor, intenta de nuevo.",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <Avatar className="w-24 h-24 ring-2 ring-primary/10">
        <AvatarImage src={url || undefined} alt={name} className="object-cover" />
        <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/30 text-primary-foreground">
          {name[0]}
        </AvatarFallback>
      </Avatar>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="relative overflow-hidden"
          disabled={uploading}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Camera className="h-4 w-4 mr-2" />
              Cambiar Avatar
              <input
                type="file"
                className="absolute inset-0 opacity-0 cursor-pointer"
                accept="image/*"
                onChange={uploadAvatar}
                disabled={uploading}
              />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
